import { useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { bedrijfSleutels } from './klanten.ts';
import { useApiMutatie } from './mutaties.ts';
import type { VacaturepaginaInvoer, VacaturepaginaWijziging } from './typen.ts';

export const vacaturepaginaSleutels = {
  vanBedrijf: (bedrijfId: string) => [...bedrijfSleutels.details(bedrijfId), 'vacaturepaginas'] as const,
};

/**
 * Somt de vacaturepagina's van een bedrijf op, met de vacatures die erop staan.
 * @param bedrijfId Het id van het bedrijf.
 * @returns De query met de pagina's.
 */
export function useVacaturepaginas(bedrijfId: string) {
  return useQuery({
    queryKey: vacaturepaginaSleutels.vanBedrijf(bedrijfId),
    queryFn: () =>
      uitvoeren(api.GET('/api/v1/bedrijven/{id}/vacaturepaginas', { params: { path: { id: bedrijfId } } })),
  });
}

/**
 * De mutaties op de vacaturepagina's van een bedrijf. Na elke mutatie worden de pagina's en de vacatures
 * ververst, omdat lezen vacatures kan toevoegen of aanvullen.
 * @param bedrijfId Het id van het bedrijf.
 * @returns Toevoegen, wijzigen, verwijderen en nu lezen.
 */
export function useVacaturepaginaMutaties(bedrijfId: string) {
  const verversen = () => [vacaturepaginaSleutels.vanBedrijf(bedrijfId), ['vacatures']];
  return {
    voegToe: useApiMutatie(
      (invoer: VacaturepaginaInvoer) =>
        uitvoeren(
          api.POST('/api/v1/bedrijven/{id}/vacaturepaginas', { params: { path: { id: bedrijfId } }, body: invoer }),
        ),
      verversen,
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: VacaturepaginaWijziging }) =>
        uitvoeren(api.PATCH('/api/v1/vacaturepaginas/{id}', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/vacaturepaginas/{id}', { params: { path: { id } } })),
      verversen,
    ),
    lees: useApiMutatie(
      (id: string) => uitvoeren(api.POST('/api/v1/vacaturepaginas/{id}/lezen', { params: { path: { id } } })),
      verversen,
    ),
  };
}
